/**
 * Token Manager - Handles JWT token lifecycle and expiration
 */

import authService from '../services/authService';
import { fetchAuthSession } from '@aws-amplify/auth';

class TokenManager {
  constructor() {
    this.tokenCheckInterval = null;
    this.tokenExpiryWarningTime = 5 * 60 * 1000; // 5 minutes before expiry
    this.tokenCheckFrequency = 60 * 1000; // Check every minute
    this.onTokenExpiredCallback = null;
    this.onTokenRefreshedCallback = null;
  }

  /**
   * Start monitoring token expiration
   */
  startMonitoring(onTokenExpired, onTokenRefreshed) {
    this.onTokenExpiredCallback = onTokenExpired;
    this.onTokenRefreshedCallback = onTokenRefreshed;

    // Clear any existing interval
    this.stopMonitoring();

    // Check immediately
    this.checkToken();

    // Then check periodically
    this.tokenCheckInterval = setInterval(() => {
      this.checkToken();
    }, this.tokenCheckFrequency);

    if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
      console.log('🔐 Token monitoring started');
    }
  }

  /**
   * Stop monitoring token expiration
   */
  stopMonitoring() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔐 Token monitoring stopped');
      }
    }
  }

  /**
   * Check token validity and refresh if needed
   */
  async checkToken() {
    try {
      const tokenInfo = await this.getTokenInfo();
      
      if (!tokenInfo) {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('🔐 No token found');
        }
        return { valid: false, expired: true };
      }

      const { exp, timeUntilExpiry } = tokenInfo;
      const now = Math.floor(Date.now() / 1000);

      // Token is expired
      if (exp <= now) {
        console.warn('🔐 Token has expired');
        if (this.onTokenExpiredCallback) {
          this.onTokenExpiredCallback();
        }
        return { valid: false, expired: true };
      }

      // Token is about to expire, try to refresh
      if (timeUntilExpiry < this.tokenExpiryWarningTime) {
        if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
          console.log('🔐 Token expiring soon, attempting refresh...');
        }
        const refreshed = await this.refreshToken();
        
        if (refreshed) {
          console.log('✅ Token refreshed successfully');
          if (this.onTokenRefreshedCallback) {
            this.onTokenRefreshedCallback();
          }
          return { valid: true, expired: false, refreshed: true };
        } else {
          // Token refresh didn't return a new token, but that's OK if we still have time
          if (timeUntilExpiry < 60000) { // Less than 1 minute left
            console.warn('⚠️ Token refresh failed, will expire in', Math.floor(timeUntilExpiry / 1000), 'seconds');
          } else if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('🔐 Token not refreshed yet, but still valid for', Math.floor(timeUntilExpiry / 1000), 'seconds');
          }
          return { valid: true, expired: false, expiringSoon: true };
        }
      }

      // Token is valid
      return { valid: true, expired: false };

    } catch (error) {
      console.error('🔐 Error checking token:', error);
      return { valid: false, expired: true, error: error.message };
    }
  }

  /**
   * Get token information (expiry time, etc.)
   */
  async getTokenInfo() {
    try {
      const token = await authService.getToken();
      if (!token) return null;

      // Decode JWT to get expiry
      const decoded = this.decodeJWT(token);
      if (!decoded || !decoded.exp) {
        console.warn('🔐 Invalid token format');
        return null;
      }

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = (decoded.exp - now) * 1000; // in milliseconds

      return {
        exp: decoded.exp,
        iat: decoded.iat,
        timeUntilExpiry,
        isExpired: decoded.exp <= now
      };

    } catch (error) {
      console.error('🔐 Error getting token info:', error);
      return null;
    }
  }

  /**
   * Refresh the authentication token
   */
  async refreshToken() {
    try {
      if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
        console.log('🔐 Attempting to refresh token...');
      }

      // Check SAML authentication first (uses different client ID)
      const samlAuth = await authService.checkSAMLAuthentication();
      if (samlAuth.success && samlAuth.tokens?.id_token) {
        // SAML tokens are valid, no need to call fetchAuthSession
        return false;
      }

      // Force a token refresh
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (session?.tokens?.idToken) {
        const newToken = session.tokens.idToken.toString();
        const currentToken = await authService.getToken();
        
        // Only update if we got a different token
        if (newToken !== currentToken) {
          authService.token = newToken;
          localStorage.setItem('jwt_token', newToken);
          
          if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('✅ Token refreshed and stored');
          }
          return true;
        } else {
          // Same token returned - Cognito thinks it's still valid
          if (process.env.REACT_APP_VERBOSE_LOGS === 'true') {
            console.log('🔐 Token still valid, no new token issued');
          }
          return true; // Return true since the session is still valid
        }
      }

      // No token in response - session may have expired
      console.warn('⚠️ No token in refresh response - session may have expired');
      
      // Check if user is still authenticated
      if (!session?.tokens) {
        console.warn('⚠️ No auth session available - user may need to sign in again');
      }
      
      return false;

    } catch (error) {
      console.error('❌ Token refresh failed:', error.message || error);
      return false;
    }
  }

  /**
   * Decode JWT token
   */
  decodeJWT(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('🔐 Failed to decode JWT:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  async isTokenExpired() {
    const tokenInfo = await this.getTokenInfo();
    return !tokenInfo || tokenInfo.isExpired;
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  async getTimeUntilExpiry() {
    const tokenInfo = await this.getTokenInfo();
    return tokenInfo ? tokenInfo.timeUntilExpiry : 0;
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
export default tokenManager;
